import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { FidcAlertModal } from 'v2/components/Shared/FidcAlertModal';
import { sendEventToGA } from 'v2/helpers/analytics';
import { useQuery } from 'v2/hooks/useQuery';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';
import { transferService } from 'v2/services/transfer';

import ValueCard from '../ValueCard';
import { THEME_COLORS } from '../ValueCard/constants';
import styles from './styles';
import TransferModal from './TransferModal';

import type { Props } from './constants';

function AvailableCard({
  isLoading,
  modalTransferIsOpen,
  hasValueToAnticipate,
  fetchData,
  handleToggleTransferModal,
  handleToggleAnticipateModal,
}: Props) {
  const classes = styles();
  const [limitToTransferPF, setLimitToTransferPF] = useState(null);
  const params = useQuery();

  const screenAction = params.get('a');

  const [modalGalaxyAlert, setModalGalaxyAlert] = useState(false);

  const [currentBalance] = useObservable(() => financialStatementService.getCurrentBalance(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);

  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');

  const [getLimitAvailableToTransfer] = useCallbackObservable(
    (currUser) =>
      transferService.getAvailablenarturalPerson().pipe(
        tap(({ data }) => {
          setLimitToTransferPF(currUser.crossborder ? data.total_origin : data.total);
        }),
        catchError((error) => {
          toast.error(error);
          return of(true);
        })
      ),
    []
  );

  const toggleTransferModal = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Transferir',
    });

    handleToggleTransferModal();
  };

  const handleOpenTransferModal = () => {
    const showGalaxyEligibleAlert = user?.eligible;
    if (showGalaxyEligibleAlert) {
      setModalGalaxyAlert(true);
      return;
    }

    toggleTransferModal();
  };

  const handleCloseTransferModal = () => {
    setModalGalaxyAlert(false);
    toggleTransferModal();
  };

  const goToTransferHistory = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Histórico de Transferências',
    });
  };

  useEffect(() => {
    if (!!user) {
      fetchData();
    }
  }, [fetchData, user]);

  useEffect(() => user?.type === 'F' && getLimitAvailableToTransfer(user), [getLimitAvailableToTransfer, user]);

  const availableToTransfer = user?.type === 'F' ? limitToTransferPF : currentBalance;

  useEffect(() => {
    if (screenAction !== 'transfer') return;
    if (!user) return;
    handleOpenTransferModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenAction, user]);

  return (
    <>
      <ValueCard
        id='available-card'
        title='Disponível para transferência'
        isLoading={isLoading}
        value={availableToTransfer}
        valueTheme={THEME_COLORS.SUCCESS}
        hint='Total disponível para saque'
        ctaCopy='Transferir'
        ctaButtonTooltip={userHasBlockTransferBetaModule ? FINANCIAL_BLOCK_TOOLTIP_MESSAGE : ''}
        isCtaDisabled={userHasBlockTransferBetaModule}
        ctaCallback={handleOpenTransferModal}
        ctaAccessControl='myeduzz:financeiro:solicitar-transferencia'
        bottomExtra={
          <>
            <Link to='/producer/financial-statement/transfer-history' onClick={goToTransferHistory} id='transfer-history-button'>
              <DescriptionOutlinedIcon className={classes.icon} />
              <span>Histórico de transferências</span>
            </Link>

            {modalTransferIsOpen && (
              <TransferModal
                isOpen={modalTransferIsOpen}
                showAnticipateStep={hasValueToAnticipate}
                setClose={handleToggleTransferModal}
                handleUpdateAvailableAmount={fetchData}
                handleToggleAnticipateModal={handleToggleAnticipateModal}
              />
            )}
          </>
        }
      />

      <FidcAlertModal isOpen={modalGalaxyAlert} onClose={handleCloseTransferModal} />
    </>
  );
}

export default AvailableCard;
